import React, { useEffect } from 'react';
import { ArrowLeft, Phone, Mail, MapPin } from 'lucide-react';
export function RefundPage() {
  useEffect(() => {
    document.title = 'Refund & Cancellation Policy | BRIXEN CREDIT LLC';
    window.scrollTo(0, 0);
  }, []);
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-900" data-id="element-926">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10" data-id="element-927">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-928">
          <a href="/" className="inline-flex items-center text-gray-600 hover:text-purple-600 font-medium transition-colors" data-id="element-929">
            <ArrowLeft className="w-5 h-5 mr-2" data-id="element-930" />
            Back to Home
          </a>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-931">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12" data-id="element-932">
          {/* Page Title */}
          <div className="border-b border-gray-100 pb-8 mb-8" data-id="element-933">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-934">
              Refund & Cancellation Policy
            </h1>
            <p className="text-sm text-gray-500 font-medium" data-id="element-935">
              Last Updated: January 30, 2026
            </p>
          </div>

          {/* Table of Contents */}
          <div className="bg-gray-50 rounded-xl p-6 mb-12" data-id="element-936">
            <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-937">
              Table of Contents
            </h2>
            <nav data-id="element-938">
              <ul className="space-y-2 text-sm md:text-base" data-id="element-939">
                {[{
                id: 'overview',
                label: '1. Overview'
              }, {
                id: 'right-to-cancel',
                label: '2. Right to Cancel (3-Day Legal Rescission)'
              }, {
                id: 'fees',
                label: '3. Fees & Billing Structure'
              }, {
                id: 'first-work-fee',
                label: '   3.1 First Work Fee',
                indent: true
              }, {
                id: 'monthly-plans',
                label: '   3.2 Monthly Subscription Plans',
                indent: true
              }, {
                id: 'express-vip',
                label: '   3.3 Express / VIP Plans',
                indent: true
              }, {
                id: 'early-termination',
                label: '4. Early Termination (Monthly Plans)'
              }, {
                id: 'guarantee',
                label: '5. 90-Day Conditional Money-Back Guarantee'
              }, {
                id: 'chargeback',
                label: '6. Zero-Tolerance Chargeback Policy'
              }, {
                id: 'no-guarantee',
                label: '7. No Guarantee of Specific Results'
              }, {
                id: 'contact',
                label: '8. Contact Information'
              }].map(item => <li key={item.id} data-id="element-940">
                    <a href={`#${item.id}`} onClick={e => scrollToSection(e, item.id)} className={`text-purple-600 hover:text-purple-800 hover:underline transition-colors ${item.indent ? 'pl-4' : ''}`} data-id="element-941">
                      {item.label}
                    </a>
                  </li>)}
              </ul>
            </nav>
          </div>

          {/* Content Sections */}
          <div className="prose prose-lg max-w-none text-gray-700 space-y-12" data-id="element-942">
            {/* Section 1 */}
            <section id="overview" className="scroll-mt-24" data-id="element-943">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-944">
                1. Overview
              </h2>
              <p className="mb-4" data-id="element-945">
                This Refund & Cancellation Policy governs all billing practices,
                refunds, cancellations, subscription terms, early termination
                conditions, guarantees, and financial obligations related to any
                service provided by BRIXEN CREDIT LLC (the "Company").
              </p>
              <p className="mb-4" data-id="element-946">
                By enrolling in any plan, submitting payment information, or
                authorizing recurring charges, the Client ("you," "your")
                acknowledges, understands, and agrees to be bound by this Policy
                in its entirety, without exception.
              </p>
              <p data-id="element-947">
                This Policy is incorporated by reference into the Company's
                Terms of Service and any service agreement executed between the
                Client and the Company.
              </p>
            </section>

            {/* Section 2 */}
            <section id="right-to-cancel" className="scroll-mt-24" data-id="element-948">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-949">
                2. Right to Cancel (3-Day Legal Rescission)
              </h2>
              <p className="mb-4" data-id="element-950">
                In full compliance with the Credit Repair Organizations Act
                (CROA), the Client has the right to cancel this agreement within
                three (3) business days from the date of enrollment without
                penalty or obligation, and to receive a 100% refund of any
                amount paid.
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-951">
                <li data-id="element-952">
                  The rescission request must be submitted in writing within the
                  three (3) business-day period.
                </li>
                <li data-id="element-953">
                  Requests received after the rescission period expires will not
                  qualify for a full refund under CROA.
                </li>
                <li data-id="element-954">
                  Once the rescission period has passed, the Client expressly
                  waives the right to a full refund except as expressly stated
                  in this Policy.
                </li>
              </ul>
            </section>

            {/* Section 3 */}
            <section id="fees" className="scroll-mt-24" data-id="element-955">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-956">
                3. Fees & Billing Structure
              </h2>

              {/* 3.1 */}
              <div id="first-work-fee" className="scroll-mt-24 mb-8" data-id="element-957">
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-958">
                  3.1 First Work Fee
                </h3>
                <p className="mb-4" data-id="element-959">
                  The First Work Fee covers professional services already
                  initiated, including but not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-960">
                  <li data-id="element-961">Initial credit audit and report analysis</li>
                  <li data-id="element-962">Client file creation and onboarding</li>
                  <li data-id="element-963">Strategic configuration and dispute preparation</li>
                </ul>
                <p className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-id="element-964">
                  <strong data-id="element-965">Important:</strong> This fee is processed 24–48 hours
                  after enrollment and becomes strictly non-refundable once the
                  3-day rescission period expires, regardless of service
                  outcome, usage level, or perceived results.
                </p>
              </div>

              {/* 3.2 */}
              <div id="monthly-plans" className="scroll-mt-24 mb-8" data-id="element-966">
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-967">
                  3.2 Monthly Subscription Plans (Basic & Premium)
                </h3>
                <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-968">
                  <li data-id="element-969">
                    Monthly subscription fees are automatically charged every
                    thirty (30) days using the payment method authorized by the
                    Client.
                  </li>
                  <li data-id="element-970">
                    Monthly plans are structured as 12-month annual service
                    programs, not month-to-month engagements.
                  </li>
                  <li data-id="element-971">
                    Enrollment in a monthly plan constitutes acknowledgment that
                    the Client is entering into an annual service commitment,
                    subject to early termination conditions outlined below.
                  </li>
                </ul>
              </div>

              {/* 3.3 */}
              <div id="express-vip" className="scroll-mt-24" data-id="element-972">
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-973">
                  3.3 Express / VIP Plans
                </h3>
                <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-974">
                  <li data-id="element-975">
                    Express and VIP plans are considered project-based,
                    accelerated service programs.
                  </li>
                  <li data-id="element-976">
                    Payments made under these plans cover work already
                    performed, resources allocated, and professional time
                    invested.
                  </li>
                  <li data-id="element-977">
                    All Express / VIP plan payments are non-refundable once
                    processing begins, regardless of results.
                  </li>
                  <li data-id="element-978">
                    Any declined, reversed, or failed payment will result in
                    immediate suspension of services, without notice.
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 4 */}
            <section id="early-termination" className="scroll-mt-24" data-id="element-979">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-980">
                4. Early Termination (Monthly Plans)
              </h2>
              <p className="mb-4" data-id="element-981">
                If the Client elects to cancel a monthly plan after the 3-day
                rescission period but before completing the full 12-month
                program, the following conditions apply:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-982">
                <li data-id="element-983">A minimum thirty (30) day written notice is required.</li>
                <li data-id="element-984">
                  An Early Termination Fee equal to{' '}
                  <strong data-id="element-985">TWO (2) MONTHLY PAYMENTS</strong> of the Client's
                  current plan will be assessed.
                </li>
                <li data-id="element-986">
                  The Client expressly authorizes the Company to charge this
                  termination fee to the payment method on file upon
                  cancellation.
                </li>
                <li data-id="element-987">
                  Failure to satisfy the Early Termination Fee may result in
                  collections activity and reporting as permitted by law.
                </li>
              </ul>
            </section>

            {/* Section 5 */}
            <section id="guarantee" className="scroll-mt-24" data-id="element-988">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-989">
                5. 90-Day Conditional Money-Back Guarantee
              </h2>
              <p className="mb-4" data-id="element-990">
                The Company offers a conditional 90-day money-back guarantee,
                subject to strict compliance with all program requirements.
              </p>
              <p className="mb-4" data-id="element-991">If, after ninety (90) consecutive days of:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-992">
                <li data-id="element-993">Active and uninterrupted service</li>
                <li data-id="element-994">Continuous, paid credit monitoring access</li>
                <li data-id="element-995">Full compliance with all client obligations</li>
              </ul>
              <p className="mb-4" data-id="element-996">
                No negative items have been removed, the Company will refund
                100% of the Client's investment within thirty (30) business
                days.
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-997">
                <p className="font-bold text-gray-900 mb-2" data-id="element-998">
                  Important Conditions:
                </p>
                <ul className="list-disc pl-6 space-y-2" data-id="element-999">
                  <li data-id="element-1000">
                    Any lapse in monitoring, missed payment, interference with
                    disputes, or non-cooperation voids this guarantee
                    immediately.
                  </li>
                  <li data-id="element-1001">
                    The guarantee applies only once per Client and does not
                    apply to Express or VIP plans.
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 6 */}
            <section id="chargeback" className="scroll-mt-24" data-id="element-1002">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1003">
                6. Zero-Tolerance Chargeback Policy
              </h2>
              <p className="mb-4" data-id="element-1004">
                Initiating a chargeback, payment dispute, or bank reversal after
                services have been initiated or work has been performed
                constitutes <strong data-id="element-1005">Service Fraud</strong>.
              </p>
              <p className="mb-4" data-id="element-1006">
                The Company maintains a zero-tolerance policy for chargebacks
                and reserves the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-1007">
                <li data-id="element-1008">
                  Submit detailed evidence, service logs, and signed agreements
                  to the issuing bank or processor
                </li>
                <li data-id="element-1009">
                  Recover all fees, penalties, administrative costs, and damages
                </li>
                <li data-id="element-1010">Refer outstanding balances to a collection agency</li>
                <li data-id="element-1011">
                  Report delinquent accounts to credit bureaus, where legally
                  permitted
                </li>
              </ul>
              <p data-id="element-1012">
                The Client acknowledges that chargebacks do not supersede
                executed agreements or federal credit repair regulations.
              </p>
            </section>

            {/* Section 7 */}
            <section id="no-guarantee" className="scroll-mt-24" data-id="element-1013">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1014">
                7. No Guarantee of Specific Results
              </h2>
              <p className="mb-4" data-id="element-1015">
                All fees charged by the Company are for professional services
                rendered, including consulting, auditing, dispute preparation,
                and compliance work.
              </p>
              <p className="mb-4" data-id="element-1016">The Company does not guarantee:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-1017">
                <li data-id="element-1018">Credit score increases</li>
                <li data-id="element-1019">Removal of specific accounts</li>
                <li data-id="element-1020">Approval for credit, loans, or financing</li>
                <li data-id="element-1021">Any particular outcome or timeline</li>
              </ul>
              <p data-id="element-1022">
                Results depend on third parties beyond the Company's control.
              </p>
            </section>

            {/* Section 8 - Contact */}
            <section id="contact" className="scroll-mt-24" data-id="element-1023">
              <h2 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-1024">
                8. Contact Information
              </h2>
              <p className="mb-6" data-id="element-1025">
                For billing, cancellation, or refund-related inquiries, contact:
              </p>

              <div className="bg-gray-50 rounded-xl p-8 border border-gray-100" data-id="element-1026">
                <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1027">
                  BRIXEN CREDIT LLC
                </h3>
                <div className="space-y-4" data-id="element-1028">
                  <div className="flex items-start" data-id="element-1029">
                    <Phone className="w-5 h-5 text-purple-600 mt-1 mr-3 flex-shrink-0" data-id="element-1030" />
                    <div data-id="element-1031">
                      <span className="block text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1032">
                        Phone
                      </span>
                      <a href="tel:786-738-6588" className="text-lg text-gray-900 hover:text-purple-600 transition-colors" data-id="element-1033">
                        786-738-6588
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start" data-id="element-1034">
                    <Mail className="w-5 h-5 text-purple-600 mt-1 mr-3 flex-shrink-0" data-id="element-1035" />
                    <div data-id="element-1036">
                      <span className="block text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1037">
                        Email
                      </span>
                      <a href="mailto:soporte@brixencredit.com" className="text-lg text-gray-900 hover:text-purple-600 transition-colors" data-id="element-1038">
                        soporte@brixencredit.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start" data-id="element-1039">
                    <MapPin className="w-5 h-5 text-purple-600 mt-1 mr-3 flex-shrink-0" data-id="element-1040" />
                    <div data-id="element-1041">
                      <span className="block text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1042">
                        Address
                      </span>
                      <span className="text-lg text-gray-900 block" data-id="element-1043">
                        390 NE 191ST ST, MIAMI, FL 33179
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>;
}